import { MANUAL_CREW_ITEMS } from "../config/quoteBudgetManualEntryConfig";
import type { ManualEntryContext, ManualEntrySource } from "../types/manualEntry";

export const CREW_ENTRY_ROLES = ["学生", "老师", "导游", "司机", "司陪"] as const;
export const CREW_ENTRY_UNITS = ["天", "晚", "次", "人"] as const;
export type CrewEntryField = "name" | "role" | "people" | "count" | "unit" | "price";
export type CrewEntryRow = { record: ManualEntrySource; name: string; role: string; people: string; count: string;
  unit: typeof CREW_ENTRY_UNITS[number]; price: string; linked: boolean; editable: boolean };
const roleKeys = { 学生: "studentCount", 老师: "teacherCount", 导游: "guideCount", 司机: "driverCount" } as const;
const valueOf = (value = "") => /^(费用名称|角色|人数|天数|晚数|次数|单价|金额)$/.test(value) ? "" : value;
const roleCount = (role: string, context: ManualEntryContext) => role in roleKeys ? String(context.people[roleKeys[role as keyof typeof roleKeys]] ?? 0) : "";
/** 按项目名称取得司导费用的固定角色与单位。 */
export const crewEntryConfig = (name: string) => MANUAL_CREW_ITEMS.find(item => item.name === name);

/** 按计费单位取得行程参考数量，未知时留空。 */
function defaultCount(unit: CrewEntryRow["unit"], context: ManualEntryContext): string {
  if (unit === "人") return "";
  if (unit === "次") return "1";
  const count = unit === "天" ? context.dayCount : context.areas?.reduce((sum, area) => sum + area.nights, 0);
  return count && Number.isSafeInteger(count) && count > 0 ? String(count) : "";
}

/** 每条司导原文独立回填，保留未完成因素，由原解析器负责金额和错误判断。 */
export function crewEntryRows(text: string, context: ManualEntryContext): CrewEntryRow[] {
  return [...text.matchAll(/[^\n;；]+/gu)].filter(match => match[0].trim()).map(match => {
    const start = (match.index ?? 0) + match[0].search(/\S/u), raw = match[0].trim();
    const record = { text: raw, start, end: start + raw.length, line: text.slice(0, start).split("\n").length };
    const header = raw.normalize("NFKC").match(/^(\S+)\s+([\s\S]*)$/u);
    const name = valueOf(header?.[1]), config = crewEntryConfig(name);
    const body = header?.[2].trim() ?? "", roleMatch = body.match(/^(学生|老师|导游|司机|司陪|角色)\s*/u);
    const role = valueOf(roleMatch?.[1] ?? config?.role), formula = body.slice(roleMatch?.[0].length ?? 0);
    const unit = config?.unit ?? (formula.includes("晚") ? "晚" : formula.includes("次") ? "次" : formula.includes("天") ? "天" : "人");
    const factors = formula.replace(/\s/gu, "").split(/[xX×*]/u).map(part => part.replace(/(?<=\d)(?:人|天|晚|次|元)$/u, ""));
    const linked = factors.length === (unit === "人" ? 1 : 2);
    return { record, name, role, unit, linked, people: linked ? roleCount(role, context) : valueOf(factors[0]),
      count: unit === "人" ? "" : valueOf(factors.at(-2)), price: valueOf(factors.at(-1)),
      editable: Boolean(header && !/[,，]/u.test(formula) && (unit === "人" ? [1, 2] : [2, 3]).includes(factors.length)) };
  });
}

/** 修改司导字段并保留固定项目约束与人数联动。 */
export function changeCrewEntryRow(text: string, row: CrewEntryRow, field: CrewEntryField, value: string, context: ManualEntryContext): string {
  if (!row.editable || /[\n\r;；,，×*]/u.test(value)) return text;
  if (field === "name" && /\s/u.test(value)) return text;
  if (["people", "count", "price"].includes(field) && /[xX]/u.test(value)) return text;
  if (field === "role" && !(CREW_ENTRY_ROLES as readonly string[]).includes(value)) return text;
  if (field === "unit" && !(CREW_ENTRY_UNITS as readonly string[]).includes(value)) return text;
  const config = crewEntryConfig(row.name);
  if ((field === "role" && config && value !== config.role) || (field === "unit" && config && value !== config.unit)) return text;
  const next: CrewEntryRow = { ...row, [field]: value };
  const nextConfig = crewEntryConfig(next.name);
  if (field === "name" && nextConfig) { next.role = nextConfig.role; next.unit = nextConfig.unit; }
  if (field === "role" || next.role !== row.role) {
    next.linked = next.role !== "司陪";
    next.people = roleCount(next.role, context);
  }
  if (next.unit !== row.unit) next.count = defaultCount(next.unit, context);
  if (field === "people") next.linked = false;
  if (next.role === "司陪") next.linked = false;
  const count = next.count ? `${next.count}${next.unit}` : next.unit === "晚" ? "晚数" : next.unit === "次" ? "次数" : "天数";
  const formula = [!next.linked ? next.people ? `${next.people}人` : "人数" : null,
    next.unit !== "人" ? count : null, next.price || "单价"].filter(part => part !== null).join("×");
  const record = `${next.name || "费用名称"} ${next.role || "角色"} ${formula}`;
  return text.slice(0, row.record.start) + record + text.slice(row.record.end);
}

/** 按原文范围删除当前司导费用，保留其余记录。 */
export function removeCrewEntryRow(text: string, row: CrewEntryRow): string {
  let { start, end } = row.record;
  const after = text.slice(end).match(/^[\s;；]+/u)?.[0];
  if (after) end += after.length;
  else start -= text.slice(0, start).match(/[\s;；]*$/u)?.[0].length ?? 0;
  const result = text.slice(0, start) + text.slice(end);
  return /^[\s;；]*$/u.test(result) ? "" : result;
}

/** 按行程天数新增司导待补价行。 */
export function appendCrewEntryRow(text: string, context: ManualEntryContext): string {
  const days = defaultCount("天", context);
  return `${text}${text && !text.endsWith("\n") ? "\n" : ""}费用名称 导游 ${days ? `${days}天` : "天数"}×单价`;
}
